import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { OnlineUser } from '../services/consultationService';
import socketService from '../services/unifiedSocketService';
import { logger } from '../utils/logger';
import { ApiService } from '../services/api-services/ApiService';
import authService from '../services/authService';
// import { useAuthStore } from './authStore'; // Removed to avoid circular dependency
import {
  SOCKET_EVENTS,
  UserStatusChangedPayload,
  DoctorAvailabilityChangedPayload,
  UserConnectedPayload,
  UserDisconnectedPayload,
} from '../constants/socketEvents';

let doctorsRefreshPromise: Promise<void> | null = null;
let patientsRefreshPromise: Promise<void> | null = null;

type PresenceStats = {
  totalOnline: number;
  totalDoctorsAvailable: number;
  totalPatients: number;
  totalDoctors: number;
  totalAdmins: number;
};

const ensureArray = <T>(value: unknown): T[] => {
  if (Array.isArray(value)) {
    return value as T[];
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('data' in obj) {
      const nested = obj.data;
      if (Array.isArray(nested)) {
        return nested as T[];
      }
    }
    if ('doctors' in obj) {
      const nested = obj.doctors;
      if (Array.isArray(nested)) {
        return nested as T[];
      }
    }
    if ('users' in obj) {
      const nested = obj.users;
      if (Array.isArray(nested)) {
        return nested as T[];
      }
    }
  }
  return [];
};

const buildPresenceStats = (
  onlineDoctors: PresenceUser[],
  onlinePatients: PresenceUser[]
): PresenceStats => ({
  totalOnline: onlineDoctors.length + onlinePatients.length,
  totalDoctorsAvailable: onlineDoctors.filter(
    (doctor) => doctor.availabilityStatus === 'AVAILABLE' || doctor.isAvailable === true
  ).length,
  totalPatients: onlinePatients.length,
  totalDoctors: onlineDoctors.length,
  totalAdmins: 0,
});

/**
 * ÉTATS POSSIBLES DE CHARGEMENT
 */
type LoadingState = 'idle' | 'loading' | 'success' | 'error';

/**
 * STATUT DE PRÉSENCE UTILISATEUR
 */
export interface PresenceUser {
  id: string | number;
  name: string;
  email: string;
  role: 'ADMIN' | 'DOCTOR' | 'PATIENT' | 'PHARMACIST';
  phoneNumber?: string;
  residence?: string;
  availabilityStatus: 'AVAILABLE' | 'BUSY' | 'OFFLINE';
  shortNumber?: string;
  // Optional backend fields
  emailHash?: string;
  totalConsultations?: number;
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  isActive?: boolean;
  mfaEnabled?: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Presence status fields
  lastActivity?: string;
  speciality?: string;
  lastSeen?: string;
  presenceStatus?: 'ONLINE' | 'OFFLINE' | 'AWAY';
  isOnline?: boolean;
  isAvailable?: boolean;
}

/**
 * ÉTAT GLOBAL DU STORE PRÉSENCE
 */
interface PresenceState {
  // ============ DONNÉES ============
  /** Utilisateurs en ligne (docteurs) */
  onlineDoctors: PresenceUser[];
  /** Utilisateurs en ligne (patients) */
  onlinePatients: PresenceUser[];
  /** Statistiques de présence */
  stats: {
    totalOnline: number;
    totalDoctorsAvailable: number;
    totalPatients: number;
    totalDoctors: number;
    totalAdmins: number;
  } | null;

  // ============ ÉTATS DE CHARGEMENT ============
  loadingDoctors: LoadingState;
  loadingPatients: LoadingState;
  loadingStats: LoadingState;

  // ============ ERREURS ============
  errorDoctors: string | null;
  errorPatients: string | null;
  errorStats: string | null;

  // ============ THROTTLING ============
  lastDoctorsUpdate: number;
  lastPatientsUpdate: number;
  lastStatsUpdate: number;
  readonly throttleMs: number;

  // ============ ACTIONS ============
  /** Initialiser le store et les listeners */
  initialize: () => void;
  /** Nettoyer les listeners */
  cleanup: () => void;

  /** Rafraîchir les docteurs en ligne */
  refreshDoctors: () => Promise<void>;
  /** Rafraîchir les patients en ligne */
  refreshPatients: () => Promise<void>;
  /** Rafraîchir tous les utilisateurs */
  refreshAll: () => Promise<void>;
  /** Rafraîchir les statistiques */
  refreshStats: () => Promise<void>;

  /** Mettre à jour la disponibilité d'un docteur */
  updateDoctorAvailability: (
    doctorId: string | number,
    availabilityStatus: 'AVAILABLE' | 'BUSY' | 'OFFLINE',
    isAvailable: boolean
  ) => void;
  /** Mettre à jour le statut d'un utilisateur */
  updateUserStatus: (userId: string | number, status: 'ONLINE' | 'OFFLINE' | 'AWAY') => void;
  /** Supprimer un utilisateur des listes */
  removeUser: (userId: string | number) => void;

  /** Réinitialiser les erreurs */
  clearErrors: () => void;
}

type PresenceStoreInternalState = PresenceState & {
  _initialized?: boolean;
  _cleanup?: () => void;
};

/**
 * STORE ZUSTAND POUR LA GESTION DE PRÉSENCE
 *
 * État centralisé remplaçant les hooks complexes useUserPresence.
 * Utilise subscribeWithSelector pour optimisations automatiques.
 *
 * @version 1.0.0
 * @since Phase 1 - Simplification Architecturale
 */
export const usePresenceStore = create<PresenceState>()(
  subscribeWithSelector((set, get) => ({
    // ============ ÉTAT INITIAL ============
    onlineDoctors: [],
    onlinePatients: [],
    stats: null,

    loadingDoctors: 'idle',
    loadingPatients: 'idle',
    loadingStats: 'idle',

    errorDoctors: null,
    errorPatients: null,
    errorStats: null,

    lastDoctorsUpdate: 0,
    lastPatientsUpdate: 0,
    lastStatsUpdate: 0,
    throttleMs: 30000, // 30 secondes (augmenté de 10s à 30s pour stabiliser le MVP)

    // ============ ACTIONS ============

    initialize: () => {
      const internalState = get() as PresenceStoreInternalState;
      if (internalState._initialized) {
        logger.debug('🎯 [usePresenceStore] Initialisation ignorée (déjà active)');
        return;
      }

      // ✅ CORRECTION: Vérifier l'authentification AVANT de lancer les requêtes
      // Sans token, les requêtes API échoueront en 401 et déclencheront
      // une cascade de refresh tokens inutiles
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        logger.warn('🚫 [usePresenceStore] Pas de token trouvé — initialisation reportée');
        return;
      }

      (internalState as PresenceStoreInternalState)._initialized = true;
      logger.info('🎯 [usePresenceStore] Initialisation du store présence - DÉBUT');
      logger.info('🔑 [usePresenceStore] Token disponible:', !!token);

      // Fonction utilitaire pour throttling
      const shouldThrottle = (lastUpdate: number): boolean => {
        const now = Date.now();
        return now - lastUpdate < get().throttleMs;
      };

      // Handlers Socket.IO
      const handleDoctorAvailabilityChanged = (...args: unknown[]) => {
        const data = args[0] as DoctorAvailabilityChangedPayload;
        logger.info('🏥 [usePresenceStore] Changement disponibilité docteur', {
          doctorId: data.doctorId,
          availabilityStatus: data.availabilityStatus,
          isAvailable: data.isAvailable,
        });

        set((state) => ({
          onlineDoctors: state.onlineDoctors.map((doctor) =>
            String(doctor.id) === String(data.doctorId)
              ? {
                  ...doctor,
                  availabilityStatus: data.availabilityStatus || 'OFFLINE',
                  isAvailable: data.isAvailable,
                }
              : doctor
          ),
          stats: buildPresenceStats(
            state.onlineDoctors.map((doctor) =>
              String(doctor.id) === String(data.doctorId)
                ? {
                    ...doctor,
                    availabilityStatus: data.availabilityStatus || 'OFFLINE',
                    isAvailable: data.isAvailable,
                  }
                : doctor
            ),
            state.onlinePatients
          ),
        }));

        // Suppression du refresh systématique (Polling agressif)
        // Les données locales sont déjà mises à jour atomiquement ci-dessus.
        logger.debug('✅ [usePresenceStore] Local state updated for doctor availability');
      };

      const handleUserStatusChanged = (...args: unknown[]) => {
        const data = args[0] as UserStatusChangedPayload;
        logger.info('� [usePresenceStore] Changement statut utilisateur', {
          userId: data.userId,
          status: data.status,
          role: data.role,
        });

        if (data.status === 'OFFLINE') {
          get().removeUser(data.userId);
        } else {
          get().updateUserStatus(data.userId, data.status);
          // Ne plus déclencher de refresh global ici.
          // L'état local est mis à jour par updateUserStatus.
          logger.debug('✅ [usePresenceStore] Local state updated for user status');
        }
      };

      const handleUserConnected = (...args: unknown[]) => {
        const data = args[0] as UserConnectedPayload;
        logger.debug('✅ [usePresenceStore] Utilisateur connecté', {
          userId: data.userId,
          role: data.role,
        });

        if (data.userId) {
          // Si l'utilisateur est déjà dans notre liste, on se contente de le passer ONLINE
          const state = get();
          const exists = data.role === 'DOCTOR' 
            ? state.onlineDoctors.some(d => String(d.id) === String(data.userId))
            : state.onlinePatients.some(p => String(p.id) === String(data.userId));

          if (exists) {
            get().updateUserStatus(data.userId, 'ONLINE');
            logger.debug('✅ [usePresenceStore] Local state updated for existing user connected');
          } else {
            // NOUVEAUTÉ ZERO-LATENCY : Injection directe sans requête HTTP throttlée
            logger.debug('⚡ [usePresenceStore] Utilisateur inconnu, ajout immédiat Zéro-Latence');
            
            const newUser: PresenceUser = {
              id: data.userId,
              name: data.name || (data.role === 'DOCTOR' ? `Médecin ${data.userId}` : `Patient ${data.userId}`),
              email: data.email || '',
              role: data.role,
              availabilityStatus: data.availabilityStatus || 'OFFLINE',
              isAvailable: data.isAvailable || (data.availabilityStatus === 'AVAILABLE'),
              presenceStatus: 'ONLINE',
              isOnline: true,
              lastSeen: new Date().toISOString()
            };

            set((currentState) => {
              const updatedDoctors = data.role === 'DOCTOR' 
                ? [...currentState.onlineDoctors, newUser] 
                : currentState.onlineDoctors;
              const updatedPatients = data.role !== 'DOCTOR' 
                ? [...currentState.onlinePatients, newUser] 
                : currentState.onlinePatients;

              return {
                onlineDoctors: updatedDoctors,
                onlinePatients: updatedPatients,
                stats: buildPresenceStats(updatedDoctors, updatedPatients)
              };
            });
          }
        }
      };

      const handleUserDisconnected = (...args: unknown[]) => {
        const data = args[0] as UserDisconnectedPayload;
        logger.info('❌ [usePresenceStore] Utilisateur déconnecté', {
          userId: data.userId,
        });

        get().removeUser(data.userId);
        // Éviter refreshAll() systématique - removeUser suffit pour la cohérence locale
        // Stats seront mises à jour lors du prochain refresh naturel ou manuel
      };

      const handleAuthenticated = () => {
        logger.info('🔑 [usePresenceStore] Socket authentifié, resynchronisation complète...');
        get().refreshAll().catch((err) => logger.error('❌ Erreur resync auth:', err));
      };

      // S'abonner aux événements Socket.IO
      socketService.on(SOCKET_EVENTS.DOCTOR_AVAILABILITY_CHANGED, handleDoctorAvailabilityChanged);
      socketService.on(SOCKET_EVENTS.USER_STATUS_CHANGED, handleUserStatusChanged);
      socketService.on(SOCKET_EVENTS.USER_CONNECTED, handleUserConnected);
      socketService.on(SOCKET_EVENTS.USER_DISCONNECTED, handleUserDisconnected);
      socketService.on(SOCKET_EVENTS.AUTHENTICATED, handleAuthenticated);

      logger.info('🎧 [usePresenceStore] Listeners Socket.IO activés');

      // CHARGEMENT AUTOMATIQUE DES DONNÉES AU DÉMARRAGE
      logger.info('📥 [usePresenceStore] Chargement automatique des données - DÉBUT');
      Promise.allSettled([get().refreshDoctors(), get().refreshPatients()])
        .then(() => {
          logger.info('📥 [usePresenceStore] Chargement automatique des données - TERMINÉ');
        })
        .catch((error) => {
          logger.error('📥 [usePresenceStore] Erreur lors du chargement automatique:', error);
        });

      // Cleanup function
      const cleanup = () => {
        socketService.off(
          SOCKET_EVENTS.DOCTOR_AVAILABILITY_CHANGED,
          handleDoctorAvailabilityChanged as (...args: unknown[]) => void
        );
        socketService.off(
          SOCKET_EVENTS.USER_STATUS_CHANGED,
          handleUserStatusChanged as (...args: unknown[]) => void
        );
        socketService.off(
          SOCKET_EVENTS.USER_CONNECTED,
          handleUserConnected as (...args: unknown[]) => void
        );
        socketService.off(
          SOCKET_EVENTS.USER_DISCONNECTED,
          handleUserDisconnected as (...args: unknown[]) => void
        );
        socketService.off(
          SOCKET_EVENTS.AUTHENTICATED,
          handleAuthenticated as (...args: unknown[]) => void
        );
        logger.info('🔇 [usePresenceStore] Listeners Socket.IO nettoyés');
      };

      // Stocker cleanup pour pouvoir l'appeler plus tard
      (internalState as PresenceStoreInternalState)._cleanup = cleanup;

      // ✅ CORRECTION: Suppression du second refreshAll() dupliqué
      // Le Promise.allSettled([refreshDoctors(), refreshPatients()]) ci-dessus (ligne 356)
      // fait déjà exactement le même travail. Ce doublon causait 2x plus de requêtes.

      logger.info('🎯 [usePresenceStore] Initialisation du store présence - TERMINÉ');
    },

    cleanup: () => {
      const internalState = get() as PresenceStoreInternalState;
      const cleanupFn = internalState._cleanup as (() => void) | undefined;
      if (cleanupFn) {
        cleanupFn();
      }

      // Reset state
      set({
        onlineDoctors: [],
        onlinePatients: [],
        stats: null,
        loadingDoctors: 'idle',
        loadingPatients: 'idle',
        loadingStats: 'idle',
        errorDoctors: null,
        errorPatients: null,
        errorStats: null,
        lastDoctorsUpdate: 0,
        lastPatientsUpdate: 0,
        lastStatsUpdate: 0,
      });
      internalState._initialized = false;
      internalState._cleanup = undefined;

      logger.debug('🧹 [usePresenceStore] Store nettoyé');
    },

    refreshDoctors: async () => {
      // ✅ Sécurité: la logique de polling a été supprimée, ce refresh n'a lieu qu'une seule fois au chargement
      // L'API Axios s'occupera du token et du rafraîchissement au besoin via son intercepteur
      if (doctorsRefreshPromise) {
        logger.debug('🚫 [usePresenceStore] Refresh doctors déjà en cours');
        return doctorsRefreshPromise;
      }

      const now = Date.now();
      if (now - get().lastDoctorsUpdate < get().throttleMs) {
        logger.debug('🚫 [usePresenceStore] Refresh doctors throttled');
        return;
      }

      logger.info('🔄 [usePresenceStore] Refresh doctors - DÉBUT');
      set({ loadingDoctors: 'loading', errorDoctors: null, lastDoctorsUpdate: now });

      doctorsRefreshPromise = (async () => {
        try {
          logger.info('🌐 [usePresenceStore] Appel API /api/users/doctors');
          const result = await ApiService.getInstance().get<OnlineUser[] | { data?: OnlineUser[] }>(
            '/api/users/doctors'
          );
          const doctors = ensureArray<OnlineUser>(result);
          logger.info(`✅ [usePresenceStore] API doctors réussie: ${doctors.length} médecins`);

          set({
            onlineDoctors: doctors as unknown as PresenceUser[],
            stats: buildPresenceStats(doctors as unknown as PresenceUser[], get().onlinePatients),
            loadingDoctors: 'success',
          });

          logger.info(`✅ [usePresenceStore] ${doctors.length} médecins rafraîchis`);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
          logger.error('❌ [usePresenceStore] Erreur refresh doctors:', error);
          set({
            loadingDoctors: 'error',
            errorDoctors: errorMessage,
          });
        } finally {
          doctorsRefreshPromise = null;
        }
      })();

      return doctorsRefreshPromise;
    },

    refreshPatients: async () => {
      // ✅ Sécurité: la logique de polling a été supprimée, ce refresh n'a lieu qu'une seule fois au chargement
      // L'API Axios s'occupera du token et du rafraîchissement au besoin via son intercepteur
      if (patientsRefreshPromise) {
        logger.debug('🚫 [usePresenceStore] Refresh patients déjà en cours');
        return patientsRefreshPromise;
      }

      const now = Date.now();
      if (now - get().lastPatientsUpdate < get().throttleMs) {
        logger.debug('🚫 [usePresenceStore] Refresh patients throttled');
        return;
      }

      logger.info('🔄 [usePresenceStore] Refresh patients - DÉBUT');
      set({ loadingPatients: 'loading', errorPatients: null, lastPatientsUpdate: now });

      patientsRefreshPromise = (async () => {
        try {
          logger.info('🌐 [usePresenceStore] Appel API /api/users/online');
          const result = await ApiService.getInstance().get<OnlineUser[] | { data?: OnlineUser[] }>(
            '/api/users/online'
          );
          const patients = ensureArray<OnlineUser>(result);
          const filteredPatients = (patients as unknown as PresenceUser[]).filter(
            (p) => p.role === 'PATIENT'
          );
          logger.info(
            `✅ [usePresenceStore] API patients réussie: ${patients.length} utilisateurs online`
          );

          set({
            onlinePatients: filteredPatients,
            stats: buildPresenceStats(get().onlineDoctors, filteredPatients),
            loadingPatients: 'success',
          });

          logger.info(`✅ [usePresenceStore] ${filteredPatients.length} patients rafraîchis`);
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
          logger.error('❌ [usePresenceStore] Erreur refresh patients:', error);
          set({
            loadingPatients: 'error',
            errorPatients: errorMessage,
          });
        } finally {
          patientsRefreshPromise = null;
        }
      })();

      return patientsRefreshPromise;
    },

    refreshAll: async () => {
      await get().refreshDoctors();
      await get().refreshPatients();
    },

    refreshStats: async () => {
      // DÉSACTIVÉ : Les statistiques sont maintenant calculées localement
      // lors de refreshDoctors() et refreshPatients() pour économiser des appels API.
      logger.debug('🚫 [usePresenceStore] refreshStats ignoré (calcul local uniquement)');
    },

    updateDoctorAvailability: (
      doctorId: string | number,
      availabilityStatus: 'AVAILABLE' | 'BUSY' | 'OFFLINE',
      isAvailable: boolean
    ) => {
      set((state) => ({
        onlineDoctors: state.onlineDoctors.map((doctor) =>
          String(doctor.id) === String(doctorId) ? { ...doctor, availabilityStatus, isAvailable } : doctor
        ),
        stats: buildPresenceStats(
          state.onlineDoctors.map((doctor) =>
            String(doctor.id) === String(doctorId) ? { ...doctor, availabilityStatus, isAvailable } : doctor
          ),
          state.onlinePatients
        ),
      }));
      logger.debug(
        `🔄 [usePresenceStore] Disponibilité docteur ${doctorId}: ${availabilityStatus}`
      );
    },

    updateUserStatus: (userId: string | number, status: 'ONLINE' | 'OFFLINE' | 'AWAY') => {
      const updater = (users: PresenceUser[]) =>
        users.map((user) =>
          String(user.id) === String(userId)
            ? { ...user, presenceStatus: status, isOnline: status === 'ONLINE' }
            : user
        );

      set((state) => ({
        onlineDoctors: updater(state.onlineDoctors),
        onlinePatients: updater(state.onlinePatients),
        stats: buildPresenceStats(updater(state.onlineDoctors), updater(state.onlinePatients)),
      }));

      logger.debug(`🔄 [usePresenceStore] Statut utilisateur ${userId}: ${status}`);
    },

    removeUser: (userId: string | number) => {
      set((state) => ({
        onlineDoctors: state.onlineDoctors.filter((doctor) => String(doctor.id) !== String(userId)),
        onlinePatients: state.onlinePatients.filter((patient) => String(patient.id) !== String(userId)),
        stats: buildPresenceStats(
          state.onlineDoctors.filter((doctor) => String(doctor.id) !== String(userId)),
          state.onlinePatients.filter((patient) => String(patient.id) !== String(userId))
        ),
      }));

      logger.debug(`🗑️ [usePresenceStore] Utilisateur ${userId} supprimé des listes`);
    },

    clearErrors: () => {
      set({
        errorDoctors: null,
        errorPatients: null,
        errorStats: null,
      });
      logger.debug('🧹 [usePresenceStore] Erreurs nettoyées');
    },
  }))
);

/**
 * HOOKS CONVENIENCE - Raccourcis pour utilisation courante
 */
export const useOnlineDoctors = () => usePresenceStore((state) => state.onlineDoctors);
export const useOnlinePatients = () => usePresenceStore((state) => state.onlinePatients);
export const usePresenceStats = () => usePresenceStore((state) => state.stats);
export const usePresenceLoading = () => ({
  loading: usePresenceStore(
    (state) => state.loadingDoctors === 'loading' || state.loadingPatients === 'loading'
  ),
  loadingDoctors: usePresenceStore((state) => state.loadingDoctors === 'loading'),
  loadingPatients: usePresenceStore((state) => state.loadingPatients === 'loading'),
});
export const usePresenceErrors = () => ({
  error: usePresenceStore((state) => state.errorDoctors || state.errorPatients || state.errorStats),
  errorDoctors: usePresenceStore((state) => state.errorDoctors),
  errorPatients: usePresenceStore((state) => state.errorPatients),
  errorStats: usePresenceStore((state) => state.errorStats),
});

/**
 * ACTIONS DU STORE - Pour utilisation dans composants
 */
export const presenceActions = {
  initialize: () => usePresenceStore.getState().initialize(),
  cleanup: () => usePresenceStore.getState().cleanup(),
  refreshAll: () => usePresenceStore.getState().refreshAll(),
  refreshDoctors: () => usePresenceStore.getState().refreshDoctors(),
  refreshPatients: () => usePresenceStore.getState().refreshPatients(),
  refreshStats: () => usePresenceStore.getState().refreshStats(),
  clearErrors: () => usePresenceStore.getState().clearErrors(),
};

export default usePresenceStore;

// Synchronisation avec authStore - DÉSACTIVÉ pour éviter référence circulaire
// TODO: Gérer cette logique dans un composant React qui importe les deux stores
/*
useAuthStore.subscribe((state: { isAuthenticated: boolean; user: any }) => {
  if (!state.isAuthenticated && state.user === null) {
    // Utilisateur déconnecté, nettoyer les données de présence
    logger.info('[presenceStore] User logged out, clearing presence data');
    usePresenceStore.getState().cleanup();
  }
});
*/
